import React, { useContext, useEffect } from 'react'
import * as PropTypes from 'prop-types'
import { Select, Layout } from 'antd'
import { AkaTypography, LoadingSpinner, AkaButton } from 'components'
import { useQuery } from '@apollo/react-hooks'
import { COMPARE_RELEASES_LIST } from 'modules/core/queries'
import { useTranslation } from 'react-i18next'
import theme from 'components/themeProvider/theme'
import { ContractsFilterContext } from './CatalogPage'
import { debounce } from 'hooks/helpers'

const { Option } = Select
const styles = {
	select: { width: '100%', fontFamily: 'SegoeUI, sans-serif' },
	wrapper: {
		background: theme.mainGray,
		display: 'flex',
		flexDirection: 'column',
		flex: '0 0 auto'
	},
	releaseSection: {
		padding: 16,
		titleWrapper: { display: 'flex' },
		compareRelease: { marginLeft: 8 }
	},
	contractsSection: {
		borderBottom: '1px solid lightgray',
		borderTop: '1px solid lightgray',
		padding: 16
	},
	options: { fontFamily: 'SegoeUI, sans-serif' }
}

const GlobalFilters = ({
	id,
	releaseName,
	onReleaseToCompareSelected,
	contractChoiceList,
	loadingContracts,
	isHzvCatalog,
	allowContractSelection
}) => {
	const [t] = useTranslation()
	const { selectedContractIdsList, updateContractList } = useContext(
		ContractsFilterContext
	)

	const { loading, data: { releasesToCompare = [] } = {} } = useQuery(
		COMPARE_RELEASES_LIST,
		{
			fetchPolicy: 'network-only',
			variables: { currentVersionId: id },
			onCompleted: data => {
				const releases = data?.releasesToCompare || []
				if (releases.length > 0) onReleaseToCompareSelected(releases[0])
			}
		}
	)

	const contractsChoiceDisabled = !allowContractSelection

	useEffect(() => {
		if (allowContractSelection || !contractChoiceList?.length) return
		const allContractIds = contractChoiceList.map(item => item.contractId)
		const hasAllSelected =
			selectedContractIdsList.length === allContractIds.length &&
			allContractIds.every(id => selectedContractIdsList.includes(id))
		if (!hasAllSelected) {
			updateContractList(allContractIds)
		}
	}, [
		allowContractSelection,
		contractChoiceList,
		selectedContractIdsList,
		updateContractList
	])

	const disabledTextStyle = contractsChoiceDisabled
		? { opacity: 0.45 }
		: undefined

	const handleSelected = id => {
		const release = releasesToCompare.reduce(
			(accum, current) => (current.id === id ? current : accum),
			undefined
		)
		onReleaseToCompareSelected(release)
	}

	const handleContractsUpdate = debounce(
		values => updateContractList(values),
		500
	)

	return (
		<Layout data-testid="global-filters-wrapper" style={styles.wrapper}>
			{loading && releasesToCompare && releasesToCompare.length === 0 && (
				<LoadingSpinner testId="loading-compare-releases" />
			)}
			{!loading && selectedContractIdsList && (
				<div style={styles.releaseSection}>
					<div style={styles.releaseSection.titleWrapper}>
						<AkaTypography
							data-testid="release-name"
							variant="bodySegoe2"
							gutterbottom={6}
						>
							{releaseName}
						</AkaTypography>
						<AkaTypography
							variant="bodySegoe"
							style={styles.releaseSection.compareRelease}
							gutterbottom={6}
						>
							{t('catalog.compareRelease')}
						</AkaTypography>
					</div>
					{releasesToCompare && releasesToCompare.length > 0 ? (
						<Select
							data-testid="release-select"
							dropdownMatchSelectWidth={false}
							defaultValue={releasesToCompare[0].id}
							style={styles.select}
							onChange={handleSelected}
							getPopupContainer={triggerNode =>
								triggerNode.closest('.ant-layout-sider') || document.body
							}
						>
							{releasesToCompare.map(release => {
								const displayText = release.catalogType
									? `${release.name} (${release.catalogType.displayTitle})`
									: release.name
								return (
									<Option
										style={styles.options}
										key={release.id}
										value={release.id}
									>
										{displayText}
									</Option>
								)
							})}
						</Select>
					) : (
						<AkaTypography
							data-testid="no-releases-message"
							variant="bodySegoe"
							gutterbottom={6}
						>
							{t('catalog.noReleasesToCompare')}
						</AkaTypography>
					)}
				</div>
			)}
			{loadingContracts && <LoadingSpinner />}
			{isHzvCatalog && contractChoiceList && !loadingContracts && (
				<div style={styles.contractsSection}>
					<span style={{ display: 'flex' }}>
						<AkaTypography variant="bodySegoe2" gutterbottom={5}>
							{t('catalog.filters.contracts')}
						</AkaTypography>
						{!contractsChoiceDisabled &&
							selectedContractIdsList.length !== contractChoiceList.length && (
								<AkaTypography
									variant="bodySegoe2"
									color={'primary'}
									gutterbottom={5}
									style={{ marginLeft: 2, ...disabledTextStyle }}
								>
									{t('catalog.filters.filtered')}
								</AkaTypography>
							)}
						<AkaTypography variant="bodySegoe2" gutterbottom={5}>
							:
						</AkaTypography>
					</span>

					<span>
						<Select
							data-testid="contract-select"
							showSearch={true}
							allowClear={true}
							maxTagCount={1}
							disabled={
								contractsChoiceDisabled ||
								!contractChoiceList ||
								contractChoiceList.length === 0
							}
							defaultValue={contractChoiceList.map(item => item.contractId)}
							onChange={handleContractsUpdate}
							mode="multiple"
							style={{ marginBottom: 6, ...styles.select }}
							value={selectedContractIdsList}
							getPopupContainer={triggerNode =>
								triggerNode.closest('.ant-layout-sider') || document.body
							}
						>
							{contractChoiceList.map(contract => (
								<Option
									style={styles.options}
									key={contract.contractId}
									value={contract.contractId}
								>
									{`${contract.identification}: ${contract.contractId}`}
								</Option>
							))}
						</Select>
					</span>
					{!contractsChoiceDisabled && (
						<AkaButton
							data-testid="contract-select-all"
							disabled={!contractChoiceList}
							onClick={() =>
								handleContractsUpdate(
									contractChoiceList.map(item => item.contractId)
								)
							}
						>
							{t('catalog.filters.selectAll')}
						</AkaButton>
					)}
				</div>
			)}
		</Layout>
	)
}

GlobalFilters.propTypes = {
	releaseName: PropTypes.string,
	id: PropTypes.string.isRequired,
	onReleaseToCompareSelected: PropTypes.func,
	contractChoiceList: PropTypes.array,
	loadingContracts: PropTypes.bool,
	isHzvCatalog: PropTypes.bool,
	allowContractSelection: PropTypes.bool
}

GlobalFilters.defaultProps = {
	releaseName: 'Release',
	onReleaseToCompareSelected: () => {},
	contractChoiceList: [],
	loadingContracts: true,
	isHzvCatalog: true,
	allowContractSelection: false
}

export default GlobalFilters
